import React, { useState, useEffect } from 'react';
import { ClosingProtectionLetterCard } from 'components/order/documentsPortal/cpl/ClosingProtectionLetterCard';
import { MessageDisplay } from 'components/order/documentsPortal/cpl/MessageDisplay';
import { IconType, RotatingIcon, useAsyncEffect } from 'ui-toolkit';
import { getOrderClosingProtectionLetterDataAsync } from 'API/order/documents/getOrderClosingProtectionLetterDataAsync';
import { getAllLendersAsyncAction } from '../../../store/lenders/lendersSlice';
import { useSelector } from 'react-redux';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { IMessage } from 'types/order/documents/IMessage';
import { IOrderClosingProtectionLetterData } from 'types/order/documents/IOrderClosingProtectionLetterData';
import { useAppDispatch } from 'store/store';
import { RootStore } from 'store/types';
import {
  generateClosingProtectionLetterAsync,
  TClosingProtectionLetterGenerationType,
} from 'API/order/documents/generateClosingProtectionLetterAsync';
import { ILenderCompanyData } from '../../../types/lenders/ILenderCompany';
import {
  createDefaultFetch,
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
} from 'types/rest/IFetchStatus';
import { useUpdateOrderTimberHook } from 'API/order/useUpdateOrderHook';
import { doGetAsyncAssociatedLenders } from '../../../ui-toolkit/associatedLenders/dispatch';
import { useAssociatedLendersContext } from '../../../ui-toolkit/associatedLenders/AssociatedLendersProvider';

export interface IClosingProtectionLetterContainerProps {}

export const ClosingProtectionLetterContainer: React.FC<IClosingProtectionLetterContainerProps> = () => {
  const [{ associatedLenders }, associatedLendersDispatch] = useAssociatedLendersContext();
  const [fetchStatus, setFetchStatus] = useState(createDefaultFetch());
  const [fetchError, setFetchError] = useState<Error>();
  const [message, setMessage] = useState<IMessage>();
  const [data, setData] = useState<IOrderClosingProtectionLetterData>();
  const { data: order } = useSelectCurrentOrder();
  const [clientLenders] = useState(order && order.client.id && associatedLenders[order.client.id]);
  const dispatch = useAppDispatch();
  const orderId = order.transaction.id!;
  const { action: doUpdateOrderAction } = useUpdateOrderTimberHook();
  const { lenders } = useSelector((store: RootStore) => store.usersCollection);
  useEffect(() => {
    dispatch(getAllLendersAsyncAction());
  }, [dispatch]);
  useEffect(() => {
    if (order && order.client.id && associatedLenders[order.client.id] === undefined) {
      doGetAsyncAssociatedLenders(order.client.id, associatedLendersDispatch);
    }
  }, [order, associatedLenders, associatedLendersDispatch]);
  // This is the list of lenders that can be changed to for this order
  const lenderOptions =
    clientLenders && clientLenders.length
      ? clientLenders
      : lenders.data.map((t: ILenderCompanyData) => ({ label: t.name!, value: t.id! }));

  // This is a function that will take a lender ID, send it to the server to be the new lender ID on this order, and then
  //  it will set the order store on this MINIPIECE to have the updated order.
  const updateOrderWithNewLender = async (lenderId: string) => {
    if (order && order.lender.id !== lenderId && order.transaction.id) {
      await doUpdateOrderAction(order, { lenderCompanyId: lenderId });
    }
  };
  const shouldOrderHaveBorrowerCPL = (!!order && order.property.state === 'IN') || (!!data && !!data.borrowerCPL);
  const onGenerateCPL = async (cplType: TClosingProtectionLetterGenerationType) => {
    // adding type safety since this method can technically outlive this render call
    if (data) {
      try {
        const isGenerating = await generateClosingProtectionLetterAsync(orderId, cplType);
        setMessage(
          isGenerating
            ? {
                type: 'success',
                text: 'The CPL can take up to a minute to generate. Refresh the page after a minute for the CPL to appear.',
              }
            : {
                type: 'error',
                text: 'There was an unknown error while generating CPL',
              }
        );
      } catch (e: any) {}
    }
  };

  useAsyncEffect(async () => {
    setFetchStatus(createFetchInProgress());
    try {
      const result = await getOrderClosingProtectionLetterDataAsync(orderId);
      setFetchStatus(createFetchedSuccess());
      setData(result);
    } catch (e: any) {
      setFetchError(e);
      setFetchStatus(createFetchedWithError());
    }
  }, [orderId]);

  return fetchStatus.fetching ? (
    <RotatingIcon type={IconType.Loading} />
  ) : fetchStatus.fetched && !fetchStatus.success ? (
    <MessageDisplay
      message={
        message ||
        (fetchError && { type: 'error', text: fetchError.message }) || {
          type: 'error',
          text: 'There was an unknown error.',
        }
      }
    />
  ) : fetchStatus.fetched && fetchStatus.success && !!data && data.underwriter.is_automated ? (
    <>
      <MessageDisplay message={message} />
      <ClosingProtectionLetterCard
        onGenerateCPL={onGenerateCPL}
        data={data}
        showBorrowerRow={shouldOrderHaveBorrowerCPL}
        lenderOptions={lenderOptions}
        updateOrderWithNewLender={updateOrderWithNewLender}
      />
    </>
  ) : (
    <></>
  );
};
